import {z} from "zod/v4-mini"
import {type Json} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {TemplateNode} from "#template/template-node/template-node"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BooleanLike, JsonLike, NumberLike, StringLike} from "#template/nodes/types"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {EvaluatedMetaData} from "#template/interface-descriptors"

export type MetaDataValue = StringLike | NumberLike | BooleanLike | JsonLike
export type MetaDataParameters = {[metaDataId: string]: MetaDataValue | undefined}

import {booleanLike} from "#template/nodes/boolean/marker"
import {jsonLike} from "#template/nodes/json/marker"
import {numberLike} from "#template/nodes/number/marker"
import {stringLike} from "#template/nodes/string/marker"

const definition: TemplateNodeDefinition<MetaDataParameters> = {
    nodeClass: "MetaData",
    validation: {
        parameters: z.record(z.string(), z.optional(z.union([stringLike, numberLike, booleanLike, jsonLike]))),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MetaData extends TemplateNode<MetaDataParameters> {
    public override get definition() {
        return definition
    }
}

/** Evaluate meta data key-value pairs to {[key]: value}. */
export function evaluateMetaData(
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    metaData: MetaData | undefined,
    id: string,
): BuilderInlet<EvaluatedMetaData> {
    if (!metaData) return {}
    const values: {[key: string]: BuilderInlet<string | number | boolean | Json>} = {}
    for (const [key, value] of Object.entries(metaData.parameters)) {
        if (value === undefined || value === null) continue
        values[key] = evaluator.evaluate(value)
    }
    if (Object.keys(values).length === 0) return {}
    return scope.struct(`metaData-${id}`, values)
}
